import { useEffect, useMemo, useState } from "react";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import { Notification } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import type { BatchDeleteScope } from "@types";
import { useT } from "@hooks";
import { getRecurrenceTransactions, unwrapList } from "@services/form";
import type { BatchSelectionItem } from "@screens/shared/useBatchSelection";
import {
  BatchDeleteHeader,
  IrreversibleNotice,
  ScopeOptions,
  StepProgress,
  type ScopeOption,
} from "./BatchDeleteParts";
import { BatchDeleteNotDeletedList, BatchDeleteProgress } from "./BatchDeleteResult";
import { useBatchDeleteProcess } from "./useBatchDeleteProcess";
import {
  buildBatchDeletePayload,
  countByStatus,
  DEFAULT_RECURRENCE_SCOPE,
  formatCurrency,
  groupRecurrenceSeries,
  resolveModuleKey,
  resolveStatusLabelKey,
  STATUS_ORDER,
  type BatchDeleteModule,
} from "./batchDeleteUtils";

export const BATCH_DELETE_LIMIT_EXCEEDED = "BATCH_DELETE_LIMIT_EXCEEDED";

const SINGLE_SERIES_OPTIONS: ScopeOption[] = [
  { scope: "SINGLE", labelKey: "batchDelete.scope.single.installment" },
  { scope: "FORECAST", labelKey: "batchDelete.scope.single.forecast" },
  { scope: "GROUP", labelKey: "batchDelete.scope.single.group" },
];

const STEP_OPTIONS: ScopeOption[] = [
  { scope: "GROUP", labelKey: "batchDelete.scope.step.group" },
  { scope: "SINGLE", labelKey: "batchDelete.scope.step.single" },
  { scope: "FORECAST", labelKey: "batchDelete.scope.step.forecast" },
];

type Step = { kind: "recurrence"; index: number } | { kind: "confirm" };

interface Props {
  show: boolean;
  module: BatchDeleteModule;
  items: BatchSelectionItem[];
  isFilterSelection: boolean;
  periodLabel: string;
  onCancel: () => void;
  onFinished: (options: { keepSelection: boolean }) => void;
}

const BatchDeleteDialog = ({ show, module, items, isFilterSelection, periodLabel, onCancel, onFinished }: Props) => {
  const t = useT();
  const { locale } = useIntl();
  const moduleKey = resolveModuleKey(module);
  const { state, submit, loadMoreNotDeleted, reset } = useBatchDeleteProcess();

  const series = useMemo(() => groupRecurrenceSeries(items), [items]);
  const isStepFlow = series.length > 1;
  const singleSeries = series.length === 1 ? series[0] : null;

  const [step, setStep] = useState<Step>({ kind: "confirm" });
  const [scopes, setScopes] = useState<Record<string, BatchDeleteScope>>({});
  const [installments, setInstallments] = useState<number | null>(null);

  useEffect(() => {
    if (!show) return;
    reset();
    setScopes({});
    setStep(isStepFlow ? { kind: "recurrence", index: 0 } : { kind: "confirm" });
  }, [show, isStepFlow, reset]);

  const singleRecurrenceId = singleSeries?.recurrenceId ?? null;
  const knownInstallments = singleSeries?.items.find((item) => item.totalInstallments)?.totalInstallments ?? null;

  useEffect(() => {
    setInstallments(null);
    if (!show || !singleRecurrenceId || knownInstallments) return;

    let isActive = true;
    getRecurrenceTransactions(singleRecurrenceId)
      .then((response) => {
        const total = unwrapList(response).length;
        if (isActive && total > 0) setInstallments(total);
      })
      .catch(() => undefined);

    return () => {
      isActive = false;
    };
  }, [show, singleRecurrenceId, knownInstallments]);

  const isProcessPhase = state.phase === "processing" || state.phase === "finished" || state.phase === "failed";
  const isSubmitting = state.phase === "submitting";
  const count = items.length;
  const totalAmount = items.reduce((sum, item) => sum + (item.amount || 0), 0);

  const scopeOf = (recurrenceId: string) => scopes[recurrenceId] ?? DEFAULT_RECURRENCE_SCOPE;
  const setScope = (recurrenceId: string, scope: BatchDeleteScope) =>
    setScopes((prev) => ({ ...prev, [recurrenceId]: scope }));

  const handleConfirm = async () => {
    const error = await submit(buildBatchDeletePayload(items, scopes));
    if (!error) return;

    const message = error.message ?? t("batchDelete.error.generic");
    Notification.error(message);

    if (error.code === BATCH_DELETE_LIMIT_EXCEEDED) onCancel();
  };

  const handleCloseResult = () => {
    const keepSelection = state.phase === "failed";
    reset();
    onFinished({ keepSelection });
  };

  const handleClose = () => {
    if (isSubmitting || state.phase === "processing") return;
    if (isProcessPhase) {
      handleCloseResult();
      return;
    }
    reset();
    onCancel();
  };

  const statusSummary = (() => {
    const counts = countByStatus(items);
    return STATUS_ORDER.filter((status) => counts[status] > 0)
      .map((status) => t(`batchDelete.statusCount.${moduleKey}.${status}`, { count: counts[status] }))
      .join(" · ");
  })();

  const renderSummary = () => (
    <div className="display-flex flex-column gap-15">
      <span className="text-size-14">{t("batchDelete.confirm.removes", { total: formatCurrency(totalAmount) })}</span>
      <div className="bg-lightest rounded padding-15 display-flex flex-column gap-5" data-testid="batch-delete-summary">
        <span className="text-size-14 text-medium">{statusSummary}</span>
        <span className="text-size-12 text-color-dark">
          {t(
            isFilterSelection
              ? `batchDelete.confirm.origin.filter.${moduleKey}`
              : `batchDelete.confirm.origin.list.${moduleKey}`,
            {
              period: periodLabel,
            }
          )}
        </span>
        {isStepFlow && (
          <span className="text-size-12 text-color-dark">
            {t("batchDelete.confirm.seriesDefined", { count: series.length })}
          </span>
        )}
      </div>
      {singleSeries && (
        <div className="border border-color-lighter rounded padding-15 display-flex flex-column gap-10">
          <div className="display-flex flex-column gap-5">
            <span className="text-size-14 text-medium">
              {t("batchDelete.confirm.recurringTitle", {
                count: singleSeries.items.length,
                installments: (knownInstallments ?? installments)?.toLocaleString(locale) ?? "N",
              })}
            </span>
            <span className="text-size-12 text-color-dark">
              {singleSeries.items.length === 1
                ? t(`batchDelete.confirm.recurringHint.${moduleKey}`, {
                    status: t(resolveStatusLabelKey(module, singleSeries.items[0].status)).toLocaleLowerCase(locale),
                  })
                : t(`batchDelete.confirm.recurringHintMany.${moduleKey}`, { count: singleSeries.items.length })}
            </span>
          </div>
          <ScopeOptions
            name={`batch-delete-scope-${singleSeries.recurrenceId}`}
            options={SINGLE_SERIES_OPTIONS}
            value={scopeOf(singleSeries.recurrenceId)}
            onChange={(scope) => setScope(singleSeries.recurrenceId, scope)}
          />
        </div>
      )}
      <IrreversibleNotice />
    </div>
  );

  const renderRecurrenceStep = (index: number) => {
    const current = series[index];
    const reference = current.items[0];
    const fields = [
      { label: t("table.column.date"), value: reference.date },
      { label: t("table.column.amount"), value: formatCurrency(reference.amount) },
      { label: t("table.column.category"), value: reference.category },
      { label: t("table.column.subcategory"), value: reference.subcategory },
    ];

    return (
      <div className="display-flex flex-column gap-15">
        <StepProgress total={series.length} current={index} />
        <div className="bg-lightest rounded padding-15 display-grid grid-cols-2 grid-cols-4-sm gap-15">
          {fields.map((field) => (
            <div key={field.label} className="display-flex flex-column gap-5">
              <span className="text-size-12 text-color-dark">{field.label}</span>
              <span className="text-size-14 text-medium">{field.value || "-"}</span>
            </div>
          ))}
        </div>
        <div className="border border-color-lighter rounded padding-15 display-flex flex-column gap-10">
          <span className="text-size-14 text-medium">{t("batchDelete.step.question")}</span>
          <ScopeOptions
            name={`batch-delete-scope-${current.recurrenceId}`}
            options={STEP_OPTIONS}
            value={scopeOf(current.recurrenceId)}
            onChange={(scope) => setScope(current.recurrenceId, scope)}
          />
        </div>
        <IrreversibleNotice />
      </div>
    );
  };

  const renderResultBody = () => {
    if (state.phase === "processing") return <BatchDeleteProgress state={state} />;

    if (state.phase === "failed") {
      return <span className="text-size-14">{t("batchDelete.result.failed")}</span>;
    }

    const summary = state.batch?.summary;
    const isPartial = state.batch?.status === "COMPLETED_WITH_ERRORS" || (summary?.notDeleted ?? 0) > 0;

    if (isPartial) {
      return (
        <div className="display-flex flex-column gap-15">
          <span className="text-size-14 text-medium">
            {t("batchDelete.result.partial", {
              deleted: (summary?.deleted ?? 0).toLocaleString(locale),
              notDeleted: (summary?.notDeleted ?? 0).toLocaleString(locale),
            })}
          </span>
          <BatchDeleteNotDeletedList state={state} onLoadMore={loadMoreNotDeleted} />
        </div>
      );
    }

    return (
      <div className="display-flex flex-column gap-15">
        <span className="text-size-14 text-medium">
          {t(`batchDelete.result.processed.${moduleKey}`)}{" "}
          {t("batchDelete.result.deleted", { count: summary?.deleted ?? 0 })}
        </span>
        <div
          className="display-flex flex-column gap-5 padding-15 rounded"
          style={{ backgroundColor: "var(--brand-success-decent)" }}
        >
          <span className="text-medium">{t("batchDelete.result.removedFromList")}</span>
          <span className="text-size-12 text-color-dark">{t("batchDelete.result.auditRegistered")}</span>
        </div>
      </div>
    );
  };

  const resultHeader = () => {
    if (state.phase === "processing") {
      return (
        <BatchDeleteHeader title={t("batchDelete.progress.title")} subtitle={t("batchDelete.progress.subtitle")} />
      );
    }
    if (state.phase === "failed") {
      return <BatchDeleteHeader title={t("batchDelete.result.failedTitle")} tone="danger" icon="rioglyph-error-sign" />;
    }
    if (state.batch?.status === "COMPLETED_WITH_ERRORS" || (state.batch?.summary?.notDeleted ?? 0) > 0) {
      return <BatchDeleteHeader title={t("batchDelete.result.partialTitle")} tone="warning" />;
    }
    return <BatchDeleteHeader title={t("batchDelete.result.successTitle")} tone="success" />;
  };

  const renderHeader = () => {
    if (isProcessPhase) return resultHeader();

    if (step.kind === "recurrence") {
      return (
        <BatchDeleteHeader
          title={t("batchDelete.step.title", { count: series.length })}
          subtitle={t("batchDelete.step.subtitle")}
          aside={
            <span className="badge bg-white border border-color-light text-color-darker text-normal">
              {t("batchDelete.step.position", { current: step.index + 1, total: series.length })}
            </span>
          }
        />
      );
    }

    return (
      <BatchDeleteHeader
        title={t("batchDelete.confirm.title", { count })}
        subtitle={t(`batchDelete.confirm.subtitle.${moduleKey}`)}
      />
    );
  };

  const renderBody = () => {
    if (isProcessPhase) return renderResultBody();
    if (step.kind === "recurrence") return renderRecurrenceStep(step.index);
    return renderSummary();
  };

  const cancelButton = (
    <button
      type="button"
      className="btn btn-default rounded-small width-100pct-xs"
      disabled={isSubmitting}
      onClick={handleClose}
    >
      {t("common.action.cancel")}
    </button>
  );

  const renderBackButton = (disabled: boolean, onClick: () => void) => (
    <button
      type="button"
      className="btn btn-default rounded-small width-100pct-xs"
      disabled={disabled}
      onClick={onClick}
    >
      <span className="rioglyph rioglyph-arrow-left margin-right-10" aria-hidden="true" />
      {t("batchDelete.step.back")}
    </button>
  );

  const renderFooter = () => {
    if (state.phase === "processing") return null;

    if (isProcessPhase) {
      return (
        <div className="display-flex justify-content-end">
          <button type="button" className="btn btn-primary rounded-small width-100pct-xs" onClick={handleCloseResult}>
            {t("batchDelete.result.backToList")}
          </button>
        </div>
      );
    }

    if (step.kind === "recurrence") {
      const isLast = step.index === series.length - 1;
      return (
        <div className="display-flex flex-wrap gap-10 justify-content-end">
          {cancelButton}
          {renderBackButton(step.index === 0, () => setStep({ kind: "recurrence", index: step.index - 1 }))}
          <button
            type="button"
            className="btn btn-danger rounded-small width-100pct-xs"
            onClick={() => setStep(isLast ? { kind: "confirm" } : { kind: "recurrence", index: step.index + 1 })}
          >
            {isLast
              ? t("batchDelete.step.review")
              : t("batchDelete.step.next", { current: step.index + 1, total: series.length })}
            <span className="rioglyph rioglyph-arrow-right margin-left-10" aria-hidden="true" />
          </button>
        </div>
      );
    }

    return (
      <div className="display-flex flex-wrap gap-10 justify-content-end">
        {cancelButton}
        {isStepFlow && renderBackButton(isSubmitting, () => setStep({ kind: "recurrence", index: series.length - 1 }))}
        <button
          type="button"
          className="btn btn-danger rounded-small width-100pct-xs"
          disabled={isSubmitting || count === 0}
          onClick={handleConfirm}
        >
          {isSubmitting && (
            <span className="margin-right-5">
              <Spinner isInline />
            </span>
          )}
          {isStepFlow ? t("batchDelete.confirm.submitMany", { count }) : t("batchDelete.confirm.submit")}
        </button>
      </div>
    );
  };

  return (
    <Dialog
      show={show}
      bsSize="md"
      className="dialog-centered"
      title={renderHeader()}
      body={<div className="padding-5">{renderBody()}</div>}
      footer={renderFooter()}
      onClose={handleClose}
      showCloseButton={state.phase !== "processing" && !isSubmitting}
      disableEsc={state.phase === "processing" || isSubmitting}
      useOverflow
    />
  );
};

export default BatchDeleteDialog;
